import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../services/auth';

interface Reagent {
  id: string;
  name: string;
  unit: string;
  stockQty: number;
  minLevel: number;
  packSize?: number | null;
  supplier?: string | null;
  notes?: string | null;
  isActive: boolean;
  needsRestock: boolean;
}

interface Movement {
  type: string;
  qty: number;
  at: string;
  actorName: string;
  orderId?: string | null;
  analyteCode?: string | null;
  analyteName?: string | null;
  comment?: string | null;
}

interface RecipeItem {
  reagentId: string;
  qtyPerTest: number;
}

interface LabIndicator {
  code: string;
  name: string;
  group: string;
  unit: string;
  normText: string;
  method: string;
  minValue: number | null;
  maxValue: number | null;
  isCustom: boolean;
  isOverridden: boolean;
}

const INDICATOR_GROUPS = ['Органолептичні', 'Фізико-хімічні', 'Санітарно-токсикологічні', 'Додаткові'];

interface MethodReagentT {
  reagentId: string;
  qtyPerTest: number;
}
interface LabMethodT {
  id: string;
  name: string;
  ndd: string;
  isDefault: boolean;
  reagents: MethodReagentT[];
}

const emptyIndicatorForm = {
  name: '',
  group: INDICATOR_GROUPS[0],
  unit: '',
  normText: '',
  method: '',
  minValue: '',
  maxValue: '',
};

interface LabPackage {
  id: string;
  name: string;
  codes: string[];
  price?: number | null;
}

interface Recipe {
  analyteCode: string;
  analyteName: string;
  items: RecipeItem[];
  coverageTests?: number | null;
}

const emptyReagentForm = {
  name: '',
  unit: 'мл',
  minLevel: '',
  packSize: '',
  supplier: '',
  notes: '',
  initialStock: '',
};

const inputCls =
  'rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none transition focus:border-blue-500';
const btnPrimary =
  'rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50';
const btnGhost =
  'rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50';

const parseQty = (s: string): number => {
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

// Numeric input that keeps the raw text being typed as local state, so
// intermediate strings like "0", "0.", "0.00" survive keystrokes and the user
// can enter fractional quantities (0,5 / 0,035 / 0,0005 г). The parent only
// receives the parsed number.
const DecimalInput: React.FC<{
  value: number;
  onValueChange: (n: number) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}> = ({ value, onValueChange, className, style, placeholder }) => {
  const [text, setText] = useState(value ? String(value) : '');
  // Re-sync when the external value changes to something the current text
  // doesn't represent (e.g. a different recipe/method is loaded).
  useEffect(() => {
    if (parseQty(text) !== value) setText(value ? String(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      className={className}
      style={style}
      inputMode="decimal"
      placeholder={placeholder}
      value={text}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9.,]/g, '');
        setText(raw);
        onValueChange(parseQty(raw));
      }}
    />
  );
};

export const AdminInventoryPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [tab, setTab] = useState<'reagents' | 'recipes' | 'indicators' | 'packages' | 'shopping'>('reagents');
  const [reagents, setReagents] = useState<Reagent[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [indicators, setIndicators] = useState<LabIndicator[]>([]);
  const [labPackages, setLabPackages] = useState<LabPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reagent create/edit form (inline panel).
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyReagentForm);
  const [formBusy, setFormBusy] = useState(false);

  // Stock adjust panel per reagent.
  const [adjustId, setAdjustId] = useState<string | null>(null);
  const [adjust, setAdjust] = useState({ qty: '', type: 'receipt', comment: '' });
  const [adjustBusy, setAdjustBusy] = useState(false);

  // Movements expanded per reagent.
  const [movesId, setMovesId] = useState<string | null>(null);
  const [moves, setMoves] = useState<Movement[]>([]);
  const [movesLoading, setMovesLoading] = useState(false);

  // Recipe editor.
  const [recipeCode, setRecipeCode] = useState<string | null>(null);
  const [recipeDraft, setRecipeDraft] = useState<RecipeItem[]>([]);
  const [recipeBusy, setRecipeBusy] = useState(false);

  // Indicator (показник) editor.
  const [indEdit, setIndEdit] = useState<LabIndicator | 'new' | null>(null);
  const [indForm, setIndForm] = useState(emptyIndicatorForm);
  const [indBusy, setIndBusy] = useState(false);
  // Extra methodologies (методики) of the indicator being edited.
  const [indExtras, setIndExtras] = useState<LabMethodT[]>([]);

  // Package constructor.
  const [pkgId, setPkgId] = useState<string | null>(null);
  const [pkgDraft, setPkgDraft] = useState<{ name: string; price: string; codes: Set<string> }>({ name: '', price: '', codes: new Set() });
  const [pkgBusy, setPkgBusy] = useState(false);
  const [pkgNew, setPkgNew] = useState(false);
  const [pkgNewId, setPkgNewId] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rRes, cRes, iRes, pRes] = await Promise.all([
        authFetch('/api/admin/inventory/reagents'),
        authFetch('/api/admin/inventory/recipes'),
        authFetch('/api/admin/lab/indicators'),
        authFetch('/api/admin/lab/packages'),
      ]);
      if (!rRes.ok || !cRes.ok || !iRes.ok || !pRes.ok) throw new Error('load');
      setReagents(await rRes.json());
      setRecipes(await cRes.json());
      setIndicators(await iRes.json());
      setLabPackages(await pRes.json());
    } catch {
      setError(t('admin:invErrorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reagentById = useMemo(() => new Map(reagents.map((r) => [r.id, r])), [reagents]);

  const stockStatus = (r: Reagent): 'ok' | 'low' | 'out' =>
    r.stockQty <= 0 ? 'out' : r.needsRestock ? 'low' : 'ok';

  // Up to 4 decimals so sub-milligram reagent doses (0,0005 г) display exactly.
  const fmtQty = (n: number) => n.toLocaleString('uk-UA', { maximumFractionDigits: 4 });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyReagentForm);
    setFormOpen(true);
  };

  const openEdit = (r: Reagent) => {
    setEditingId(r.id);
    setForm({
      name: r.name,
      unit: r.unit,
      minLevel: String(r.minLevel || ''),
      packSize: r.packSize ? String(r.packSize) : '',
      supplier: r.supplier || '',
      notes: r.notes || '',
      initialStock: '',
    });
    setFormOpen(true);
  };

  const saveReagent = async () => {
    setFormBusy(true);
    try {
      const body = {
        name: form.name,
        unit: form.unit,
        minLevel: Number(form.minLevel.replace(',', '.')) || 0,
        packSize: form.packSize ? Number(form.packSize.replace(',', '.')) : null,
        supplier: form.supplier || null,
        notes: form.notes || null,
        initialStock: !editingId && form.initialStock ? Number(form.initialStock.replace(',', '.')) : null,
      };
      const res = await authFetch(
        editingId ? `/api/admin/inventory/reagents/${editingId}` : '/api/admin/inventory/reagents',
        { method: editingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:invErrorSave'));
    } finally {
      setFormBusy(false);
    }
  };

  const deleteReagent = async (r: Reagent) => {
    if (!window.confirm(t('admin:invDeleteConfirm', { name: r.name }))) return;
    try {
      const res = await authFetch(`/api/admin/inventory/reagents/${r.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:invErrorSave'));
    }
  };

  const applyAdjust = async () => {
    if (!adjustId) return;
    const qtyRaw = Number(adjust.qty.replace(',', '.'));
    if (!qtyRaw) return;
    // Receipts add, write-offs subtract; adjustment uses the sign as entered.
    const qty = adjust.type === 'writeoff' ? -Math.abs(qtyRaw) : adjust.type === 'receipt' ? Math.abs(qtyRaw) : qtyRaw;
    setAdjustBusy(true);
    try {
      const res = await authFetch(`/api/admin/inventory/reagents/${adjustId}/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qty, type: adjust.type, comment: adjust.comment || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const updated: Reagent = await res.json();
      setReagents((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setAdjustId(null);
      setAdjust({ qty: '', type: 'receipt', comment: '' });
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:invErrorSave'));
    } finally {
      setAdjustBusy(false);
    }
  };

  const toggleMoves = async (id: string) => {
    if (movesId === id) {
      setMovesId(null);
      return;
    }
    setMovesId(id);
    setMoves([]);
    setMovesLoading(true);
    try {
      const res = await authFetch(`/api/admin/inventory/reagents/${id}/movements`);
      if (res.ok) setMoves(await res.json());
    } finally {
      setMovesLoading(false);
    }
  };

  const openRecipe = (code: string) => {
    const recipe = recipes.find((r) => r.analyteCode === code);
    setRecipeCode(code);
    setRecipeDraft(recipe ? recipe.items.map((i) => ({ ...i })) : []);
  };

  const saveRecipe = async () => {
    if (!recipeCode) return;
    setRecipeBusy(true);
    try {
      const res = await authFetch(`/api/admin/inventory/recipes/${recipeCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: recipeDraft.filter((i) => i.reagentId && i.qtyPerTest > 0) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const updated: Recipe = await res.json();
      setRecipes((prev) => prev.map((r) => (r.analyteCode === updated.analyteCode ? updated : r)));
      setRecipeDraft(updated.items.map((i) => ({ ...i })));
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:invErrorSave'));
    } finally {
      setRecipeBusy(false);
    }
  };

  const groupedIndicators = useMemo(() => {
    const groups: { group: string; items: LabIndicator[] }[] = [];
    indicators.forEach((i) => {
      const g = groups.find((x) => x.group === i.group);
      if (g) g.items.push(i);
      else groups.push({ group: i.group, items: [i] });
    });
    return groups;
  }, [indicators]);

  const openPkg = (id: string) => {
    const pkg = labPackages.find((p) => p.id === id);
    setPkgNew(false);
    setPkgId(id);
    setPkgDraft({ name: pkg?.name || '', price: pkg?.price != null ? String(pkg.price) : '', codes: new Set(pkg?.codes || []) });
  };

  const startNewPkg = () => {
    setPkgId(null);
    setPkgNew(true);
    setPkgNewId('');
    setPkgDraft({ name: '', price: '', codes: new Set() });
  };

  const togglePkgCode = (code: string) => {
    setPkgDraft((d) => {
      const codes = new Set(d.codes);
      if (codes.has(code)) codes.delete(code);
      else codes.add(code);
      return { ...d, codes };
    });
  };

  const savePkg = async () => {
    const id = pkgNew ? pkgNewId.trim().toLowerCase() : pkgId;
    if (!id) return;
    setPkgBusy(true);
    try {
      const res = await authFetch(`/api/admin/lab/packages/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: pkgDraft.name,
          codes: [...pkgDraft.codes],
          price: pkgDraft.price.trim() ? Number(pkgDraft.price.replace(',', '.')) : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const updated: LabPackage = await res.json();
      setLabPackages((prev) => {
        const exists = prev.some((p) => p.id === updated.id);
        return exists ? prev.map((p) => (p.id === updated.id ? updated : p)) : [...prev, updated];
      });
      setPkgNew(false);
      setPkgId(updated.id);
      setPkgDraft({ name: updated.name, price: updated.price != null ? String(updated.price) : '', codes: new Set(updated.codes) });
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:invErrorSave'));
    } finally {
      setPkgBusy(false);
    }
  };

  const openIndCreate = () => {
    setIndForm(emptyIndicatorForm);
    setIndExtras([]);
    setIndEdit('new');
  };

  const openIndEdit = async (i: LabIndicator) => {
    setIndForm({
      name: i.name,
      group: i.group,
      unit: i.unit || '',
      normText: i.normText || '',
      method: i.method || '',
      minValue: i.minValue != null ? String(i.minValue) : '',
      maxValue: i.maxValue != null ? String(i.maxValue) : '',
    });
    setIndExtras([]);
    setIndEdit(i);
    try {
      const res = await authFetch(`/api/admin/lab/indicators/${i.code}/methods`);
      if (res.ok) {
        const list: LabMethodT[] = await res.json();
        setIndExtras(list.filter((m) => !m.isDefault));
      }
    } catch {
      /* keep the editor usable even if methods fail to load */
    }
  };

  const addExtraMethod = () =>
    setIndExtras((m) => [...m, { id: '', name: '', ndd: '', isDefault: false, reagents: [] }]);
  const updateExtra = (idx: number, patch: Partial<LabMethodT>) =>
    setIndExtras((m) => m.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeExtra = (idx: number) => setIndExtras((m) => m.filter((_, i) => i !== idx));

  const saveInd = async () => {
    if (indEdit === null) return;
    const isNew = indEdit === 'new';
    const num = (s: string) => (s.trim() ? Number(s.replace(',', '.')) : null);
    setIndBusy(true);
    try {
      const body = {
        name: indForm.name.trim() || null,
        group: indForm.group,
        unit: indForm.unit.trim() || null,
        normText: indForm.normText.trim() || null,
        method: indForm.method.trim() || null,
        minValue: num(indForm.minValue),
        maxValue: num(indForm.maxValue),
      };
      const res = await authFetch(
        isNew ? '/api/admin/lab/indicators' : `/api/admin/lab/indicators/${indEdit.code}`,
        { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      // Persist the extra methods (existing indicators only; new ones are created first).
      if (!isNew) {
        const mRes = await authFetch(`/api/admin/lab/indicators/${indEdit.code}/methods`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            methods: indExtras.map((m) => ({
              id: m.id || null,
              name: m.name.trim() || null,
              ndd: m.ndd.trim() || null,
              reagents: m.reagents.filter((r) => r.reagentId && r.qtyPerTest > 0),
            })),
          }),
        });
        if (!mRes.ok) {
          const err = await mRes.json().catch(() => ({}));
          throw new Error(err.message || `HTTP ${mRes.status}`);
        }
      }
      setIndEdit(null);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:invErrorSave'));
    } finally {
      setIndBusy(false);
    }
  };

  const deleteInd = async (i: LabIndicator) => {
    const msg = i.isCustom ? t('admin:invIndDeleteConfirm', { name: i.name }) : t('admin:invIndResetConfirm', { name: i.name });
    if (!window.confirm(msg)) return;
    try {
      const res = await authFetch(`/api/admin/lab/indicators/${i.code}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:invErrorSave'));
    }
  };

  // Shopping list: everything at/below min, suggested up to 2× the min level,
  // rounded up to whole packs when a pack size is known.
  const shopping = useMemo(
    () =>
      reagents
        .filter((r) => r.needsRestock || r.stockQty <= 0)
        .map((r) => {
          const target = r.minLevel > 0 ? r.minLevel * 2 : 0;
          const need = Math.max(target - r.stockQty, 0);
          const packs = r.packSize && r.packSize > 0 ? Math.ceil(need / r.packSize) : null;
          const buyQty = packs !== null && r.packSize ? packs * r.packSize : need;
          return { reagent: r, need, packs, buyQty };
        })
        .filter((x) => x.need > 0),
    [reagents],
  );

  const selectedRecipe = recipeCode ? recipes.find((r) => r.analyteCode === recipeCode) : null;

  const statusBadge = (r: Reagent) => {
    const s = stockStatus(r);
    const cls =
      s === 'ok'
        ? 'bg-emerald-50 text-emerald-700'
        : s === 'low'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-rose-50 text-rose-700';
    return (
      <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${cls}`}>
        {t(`admin:invStatus${s === 'ok' ? 'Ok' : s === 'low' ? 'Low' : 'Out'}`)}
      </span>
    );
  };

  return (
    <div className="admin-page od-modern">
      <div className="admin-header">
        <div>
          <h1>{t('admin:invTitle')}</h1>
          <p className="admin-subtitle">{t('admin:invSubtitle')}</p>
        </div>
        {tab === 'reagents' && (
          <button type="button" className={btnPrimary} onClick={openCreate}>
            + {t('admin:invAddReagent')}
          </button>
        )}
        {tab === 'indicators' && (
          <button type="button" className={btnPrimary} onClick={openIndCreate}>
            + {t('admin:invAddIndicator')}
          </button>
        )}
      </div>

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {(
          [
            ['reagents', 'invTabReagents'],
            ['recipes', 'invTabRecipes'],
            ['indicators', 'invTabIndicators'],
            ['packages', 'invTabPackages'],
            ['shopping', 'invTabShopping'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1 text-sm font-medium transition ${tab === key ? 'border-slate-800 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          >
            {t(`admin:${label}`)}
            {key === 'shopping' && shopping.length > 0 && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-amber-700">
                {shopping.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}

      {!loading && !error && tab === 'reagents' && (
        <div className="flex flex-col gap-4">
          {formOpen && (
            <div className="rounded-xl border border-slate-100 bg-white p-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {editingId ? t('admin:invEdit') : t('admin:invAddReagent')}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="flex flex-col gap-1 text-xs text-slate-500 sm:col-span-2">
                  {t('admin:invName')}
                  <input className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500">
                  {t('admin:invUnit')}
                  <input className={inputCls} value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} placeholder="мл / г / шт" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500">
                  {t('admin:invMinLevel')}
                  <input className={inputCls} inputMode="decimal" value={form.minLevel} onChange={(e) => setForm((f) => ({ ...f, minLevel: e.target.value }))} />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500">
                  {t('admin:invPackSize')}
                  <input className={inputCls} inputMode="decimal" value={form.packSize} onChange={(e) => setForm((f) => ({ ...f, packSize: e.target.value }))} />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500">
                  {t('admin:invSupplier')}
                  <input className={inputCls} value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} />
                </label>
                {!editingId && (
                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                    {t('admin:invInitialStock')}
                    <input className={inputCls} inputMode="decimal" value={form.initialStock} onChange={(e) => setForm((f) => ({ ...f, initialStock: e.target.value }))} />
                  </label>
                )}
                <label className="flex flex-col gap-1 text-xs text-slate-500 sm:col-span-2">
                  {t('admin:invNotes')}
                  <input className={inputCls} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                </label>
              </div>
              <div className="mt-3 flex gap-2">
                <button type="button" className={btnPrimary} disabled={formBusy || !form.name.trim() || !form.unit.trim()} onClick={saveReagent}>
                  {t('admin:invSave')}
                </button>
                <button type="button" className={btnGhost} onClick={() => setFormOpen(false)}>
                  {t('admin:cancel')}
                </button>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-100 bg-white p-4">
            {reagents.length === 0 ? (
              <p className="text-sm text-slate-400">{t('admin:invNoReagents')}</p>
            ) : (
              <div className="flex flex-col">
                <div className="hidden grid-cols-[minmax(0,1fr)_110px_110px_120px_360px] gap-2 px-2 pb-2 text-[11px] uppercase tracking-wide text-slate-400 sm:grid">
                  <span>{t('admin:invColName')}</span>
                  <span>{t('admin:invColStock')}</span>
                  <span>{t('admin:invColMin')}</span>
                  <span>{t('admin:invColStatus')}</span>
                  <span />
                </div>
                {reagents.map((r) => (
                  <React.Fragment key={r.id}>
                    <div className="grid grid-cols-2 items-center gap-2 border-t border-slate-100 px-2 py-2 sm:grid-cols-[minmax(0,1fr)_110px_110px_120px_360px]">
                      <div className="col-span-2 min-w-0 sm:col-span-1">
                        <div className="text-sm font-medium text-slate-800">{r.name}</div>
                        {(r.supplier || r.notes) && (
                          <div className="truncate text-[11px] text-slate-400">{[r.supplier, r.notes].filter(Boolean).join(' · ')}</div>
                        )}
                      </div>
                      <span className="text-sm text-slate-700">
                        {fmtQty(r.stockQty)} {r.unit}
                      </span>
                      <span className="text-sm text-slate-500">
                        {r.minLevel > 0 ? `${fmtQty(r.minLevel)} ${r.unit}` : '—'}
                      </span>
                      <span>{statusBadge(r)}</span>
                      <span className="col-span-2 flex flex-nowrap items-center justify-end gap-1.5 whitespace-nowrap sm:col-span-1">
                        <button
                          type="button"
                          title={t('admin:invReceiveTitle')}
                          className={`${btnGhost} ${adjustId === r.id ? 'border-blue-300 text-blue-700' : ''}`}
                          onClick={() => { setAdjustId(adjustId === r.id ? null : r.id); setMovesId(null); setAdjust({ qty: '', type: 'receipt', comment: '' }); }}
                        >
                          {t('admin:invReceive')}
                        </button>
                        <button
                          type="button"
                          title={t('admin:invMovesTitle')}
                          className={`${btnGhost} ${movesId === r.id ? 'border-blue-300 text-blue-700' : ''}`}
                          onClick={() => { toggleMoves(r.id); setAdjustId(null); }}
                        >
                          {t('admin:invMoves')}
                        </button>
                        <button type="button" title={t('admin:invEdit')} className={btnGhost} onClick={() => openEdit(r)}>
                          {t('admin:invEditShort')}
                        </button>
                        <button
                          type="button"
                          title={t('admin:invDelete')}
                          aria-label={t('admin:invDelete')}
                          className="rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-sm font-medium text-rose-600 transition hover:bg-rose-50"
                          onClick={() => deleteReagent(r)}
                        >
                          🗑
                        </button>
                      </span>
                    </div>

                    {adjustId === r.id && (
                      <div className="flex flex-wrap items-end gap-2 border-t border-dashed border-slate-100 bg-slate-50/60 px-3 py-2.5">
                        <label className="flex flex-col gap-1 text-xs text-slate-500">
                          {t('admin:invMoveType')}
                          <select className={inputCls} value={adjust.type} onChange={(e) => setAdjust((a) => ({ ...a, type: e.target.value }))}>
                            <option value="receipt">{t('admin:invTypeReceipt')}</option>
                            <option value="adjustment">{t('admin:invTypeAdjustment')}</option>
                            <option value="writeoff">{t('admin:invTypeWriteoff')}</option>
                          </select>
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-slate-500">
                          {t('admin:invQty')} ({r.unit})
                          <input className={inputCls} inputMode="decimal" style={{ width: 110 }} value={adjust.qty} onChange={(e) => setAdjust((a) => ({ ...a, qty: e.target.value }))} />
                        </label>
                        <label className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-slate-500">
                          {t('admin:invComment')}
                          <input className={inputCls} value={adjust.comment} onChange={(e) => setAdjust((a) => ({ ...a, comment: e.target.value }))} />
                        </label>
                        <button type="button" className={btnPrimary} disabled={adjustBusy || !adjust.qty} onClick={applyAdjust}>
                          {t('admin:invApply')}
                        </button>
                      </div>
                    )}

                    {movesId === r.id && (
                      <div className="border-t border-dashed border-slate-100 bg-slate-50/60 px-3 py-2.5">
                        {movesLoading ? (
                          <p className="text-xs text-slate-400">{t('common:loading')}</p>
                        ) : moves.length === 0 ? (
                          <p className="text-xs text-slate-400">{t('admin:invMovesEmpty')}</p>
                        ) : (
                          <div className="flex max-h-56 flex-col gap-1 overflow-y-auto">
                            {moves.map((m, i) => (
                              <div key={i} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                                <span className="text-slate-400">{new Date(m.at).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' })}</span>
                                <span className={m.qty >= 0 ? 'font-medium text-emerald-700' : 'font-medium text-rose-700'}>
                                  {m.qty >= 0 ? '+' : ''}{fmtQty(m.qty)} {r.unit}
                                </span>
                                <span className="text-slate-500">{t(`admin:invType${m.type === 'receipt' ? 'Receipt' : m.type === 'consumption' ? 'Consumption' : m.type === 'writeoff' ? 'Writeoff' : 'Adjustment'}`)}</span>
                                {m.analyteName && <span className="text-slate-500">· {m.analyteName}</span>}
                                {m.comment && <span className="text-slate-400">· {m.comment}</span>}
                                <span className="text-slate-300">· {m.actorName}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && !error && tab === 'recipes' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
          <div className="rounded-xl border border-slate-100 bg-white p-2" style={{ maxHeight: 560, overflowY: 'auto' }}>
            {recipes.map((rec) => (
              <button
                key={rec.analyteCode}
                type="button"
                onClick={() => openRecipe(rec.analyteCode)}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${recipeCode === rec.analyteCode ? 'bg-blue-50 text-blue-800' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                <span className="min-w-0 truncate">{rec.analyteName}</span>
                {rec.items.length > 0 ? (
                  <span className="shrink-0 text-[11px] text-slate-400">
                    {rec.items.length} · {rec.coverageTests != null ? t('admin:invCoverage', { count: rec.coverageTests }) : ''}
                  </span>
                ) : (
                  <span className="shrink-0 text-[11px] text-slate-300">{t('admin:invNoRecipe')}</span>
                )}
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-4">
            {!selectedRecipe ? (
              <p className="text-sm text-slate-400">{t('admin:invRecipeHint')}</p>
            ) : (
              <>
                <div className="mb-3 flex items-baseline justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-800">{selectedRecipe.analyteName}</div>
                  {selectedRecipe.coverageTests != null && (
                    <span className="text-xs text-slate-400">{t('admin:invCoverage', { count: selectedRecipe.coverageTests })}</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {recipeDraft.map((item, idx) => {
                    const reagent = reagentById.get(item.reagentId);
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <select
                          className={`${inputCls} min-w-0 flex-1`}
                          value={item.reagentId}
                          onChange={(e) => setRecipeDraft((d) => d.map((x, i) => (i === idx ? { ...x, reagentId: e.target.value } : x)))}
                        >
                          <option value="">—</option>
                          {reagents.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                        <DecimalInput
                          className={inputCls}
                          style={{ width: 100 }}
                          value={item.qtyPerTest}
                          placeholder={t('admin:invPerTest')}
                          onValueChange={(v) => setRecipeDraft((d) => d.map((x, i) => (i === idx ? { ...x, qtyPerTest: v } : x)))}
                        />
                        <span className="w-8 shrink-0 text-xs text-slate-500">{reagent?.unit || ''}</span>
                        <button type="button" className={`${btnGhost} text-rose-600`} onClick={() => setRecipeDraft((d) => d.filter((_, i) => i !== idx))}>
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button type="button" className={btnGhost} onClick={() => setRecipeDraft((d) => [...d, { reagentId: '', qtyPerTest: 0 }])}>
                    + {t('admin:invAddLine')}
                  </button>
                  <button type="button" className={btnPrimary} disabled={recipeBusy} onClick={saveRecipe}>
                    {recipeBusy ? t('admin:resultUploading') : t('admin:invSave')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {!loading && !error && tab === 'packages' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          <div className="flex flex-col gap-2">
            <div className="rounded-xl border border-slate-100 bg-white p-2">
              {labPackages.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openPkg(p.id)}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${pkgId === p.id && !pkgNew ? 'bg-blue-50 text-blue-800' : 'text-slate-700 hover:bg-slate-50'}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{p.name}</span>
                    <span className="text-[11px] text-slate-400">{p.id}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">{t('admin:invPkgCount', { count: p.codes.length })}</span>
                </button>
              ))}
            </div>
            <button type="button" className={btnGhost} onClick={startNewPkg}>
              + {t('admin:invPkgNew')}
            </button>
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-4">
            {!pkgId && !pkgNew ? (
              <p className="text-sm text-slate-400">{t('admin:invPkgHint')}</p>
            ) : (
              <>
                <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {pkgNew && (
                    <label className="flex flex-col gap-1 text-xs text-slate-500">
                      {t('admin:invPkgId')}
                      <input className={inputCls} value={pkgNewId} placeholder="well-check" onChange={(e) => setPkgNewId(e.target.value)} />
                    </label>
                  )}
                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                    {t('admin:invPkgName')}
                    <input className={inputCls} value={pkgDraft.name} onChange={(e) => setPkgDraft((d) => ({ ...d, name: e.target.value }))} />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                    {t('admin:invPkgPrice')}
                    <input className={inputCls} inputMode="decimal" value={pkgDraft.price} onChange={(e) => setPkgDraft((d) => ({ ...d, price: e.target.value }))} />
                  </label>
                </div>

                {groupedIndicators.map((g) => (
                  <div key={g.group} className="mb-2">
                    <div className="px-1 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{g.group}</div>
                    <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                      {g.items.map((i) => (
                        <label key={i.code} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-slate-700 hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={pkgDraft.codes.has(i.code)}
                            onChange={() => togglePkgCode(i.code)}
                          />
                          <span className="min-w-0 truncate">{i.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="mt-3 flex items-center gap-3 border-t border-slate-100 pt-3">
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={pkgBusy || !pkgDraft.name.trim() || pkgDraft.codes.size === 0 || (pkgNew && !pkgNewId.trim())}
                    onClick={savePkg}
                  >
                    {pkgBusy ? t('admin:resultUploading') : t('admin:invSave')}
                  </button>
                  <span className="text-xs text-slate-400">{t('admin:invPkgCount', { count: pkgDraft.codes.size })}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {!loading && !error && tab === 'indicators' && (
        <div className="rounded-xl border border-slate-100 bg-white p-4">
          <p className="mb-3 text-sm text-slate-500">{t('admin:invIndHint')}</p>
          {groupedIndicators.map((g) => (
            <div key={g.group} className="mb-4">
              <div className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{g.group}</div>
              <div className="hidden grid-cols-[minmax(0,1fr)_110px_120px_minmax(0,150px)_150px] gap-2 px-2 pb-1 text-[11px] uppercase tracking-wide text-slate-400 sm:grid">
                <span>{t('admin:invIndColName')}</span>
                <span>{t('admin:invIndColUnit')}</span>
                <span>{t('admin:invIndColNorm')}</span>
                <span>{t('admin:invIndColMethod')}</span>
                <span className="text-right">{t('admin:actions')}</span>
              </div>
              {g.items.map((i) => (
                <div
                  key={i.code}
                  className="grid grid-cols-1 items-center gap-2 border-t border-slate-100 px-2 py-2 text-sm sm:grid-cols-[minmax(0,1fr)_110px_120px_minmax(0,150px)_150px]"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-slate-800">{i.name}</span>
                    {i.isCustom ? (
                      <span className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-600">{t('admin:invIndCustom')}</span>
                    ) : i.isOverridden ? (
                      <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">{t('admin:invIndEdited')}</span>
                    ) : null}
                  </div>
                  <span className="text-slate-600">{i.unit || '—'}</span>
                  <span className="text-slate-600">{i.normText || '—'}</span>
                  <span className="truncate text-slate-500" title={i.method}>{i.method || '—'}</span>
                  <span className="flex items-center justify-end gap-1.5">
                    <button type="button" className={btnGhost} onClick={() => openIndEdit(i)}>
                      {t('admin:usersEdit')}
                    </button>
                    {(i.isCustom || i.isOverridden) && (
                      <button type="button" className={`${btnGhost} text-rose-600`} onClick={() => deleteInd(i)}>
                        {i.isCustom ? t('admin:delete') : t('admin:invIndReset')}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {indEdit !== null && (
        <div className="modal-overlay" onClick={() => !indBusy && setIndEdit(null)}>
          <div className="modal create-order-modal od-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {indEdit === 'new'
                  ? t('admin:invIndCreateTitle')
                  : t('admin:invIndEditTitle', { name: indEdit.name })}
              </h2>
              <button className="modal-close" onClick={() => setIndEdit(null)}>
                ×
              </button>
            </div>
            <div className="confirm-body create-order-body">
              {(() => {
                // Built-in indicators keep their catalog name/group (only norm/ДСТУ
                // are overridable); customs are fully editable.
                const builtIn = indEdit !== 'new' && !indEdit.isCustom;
                return (
                  <>
                    <div className="co-row">
                      <label className="form-group flex flex-col gap-1 text-xs text-slate-500">
                        {t('admin:invIndColName')}
                        <input
                          className={inputCls}
                          value={indForm.name}
                          disabled={builtIn}
                          onChange={(e) => setIndForm({ ...indForm, name: e.target.value })}
                        />
                      </label>
                      <label className="form-group flex flex-col gap-1 text-xs text-slate-500">
                        {t('admin:invIndColGroup')}
                        <select
                          className={inputCls}
                          value={indForm.group}
                          disabled={builtIn}
                          onChange={(e) => setIndForm({ ...indForm, group: e.target.value })}
                        >
                          {INDICATOR_GROUPS.map((gr) => (
                            <option key={gr} value={gr}>{gr}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="co-row">
                      <label className="form-group flex flex-col gap-1 text-xs text-slate-500">
                        {t('admin:invIndColUnit')}
                        <input className={inputCls} value={indForm.unit} placeholder="мг/дм³" onChange={(e) => setIndForm({ ...indForm, unit: e.target.value })} />
                      </label>
                      <label className="form-group flex flex-col gap-1 text-xs text-slate-500">
                        {t('admin:invIndColNorm')}
                        <input className={inputCls} value={indForm.normText} placeholder="≤ 0,5" onChange={(e) => setIndForm({ ...indForm, normText: e.target.value })} />
                      </label>
                    </div>
                    <label className="form-group flex flex-col gap-1 text-xs text-slate-500">
                      {t('admin:invIndColMethod')}
                      <input className={inputCls} value={indForm.method} placeholder="ДСТУ ISO 7027:2003" onChange={(e) => setIndForm({ ...indForm, method: e.target.value })} />
                    </label>
                    <div className="co-row">
                      <label className="form-group flex flex-col gap-1 text-xs text-slate-500">
                        {t('admin:invIndColMin')}
                        <input className={inputCls} inputMode="decimal" value={indForm.minValue} onChange={(e) => setIndForm({ ...indForm, minValue: e.target.value })} />
                      </label>
                      <label className="form-group flex flex-col gap-1 text-xs text-slate-500">
                        {t('admin:invIndColMax')}
                        <input className={inputCls} inputMode="decimal" value={indForm.maxValue} onChange={(e) => setIndForm({ ...indForm, maxValue: e.target.value })} />
                      </label>
                    </div>
                    <p className="text-xs text-slate-400">{t('admin:invIndRangeHint')}</p>
                  </>
                );
              })()}

              {indEdit !== 'new' && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:invMethodsTitle')}</div>
                  <p className="mb-2 text-xs text-slate-400">{t('admin:invMethodsHint')}</p>
                  <div className="mb-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    {t('admin:invMethodDefault')}: <span className="font-medium text-slate-800">{indForm.method || '—'}</span>
                  </div>
                  {indExtras.map((m, idx) => (
                    <div key={idx} className="mb-2 rounded-lg border border-slate-200 p-3">
                      <div className="mb-2 flex items-start gap-2">
                        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                          <input className={inputCls} placeholder={t('admin:invMethodName')} value={m.name} onChange={(e) => updateExtra(idx, { name: e.target.value })} />
                          <input className={inputCls} placeholder="ДСТУ ISO ..." value={m.ndd} onChange={(e) => updateExtra(idx, { ndd: e.target.value })} />
                        </div>
                        <button type="button" className={`${btnGhost} text-rose-600`} title={t('admin:delete')} onClick={() => removeExtra(idx)}>✕</button>
                      </div>
                      <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">{t('admin:invMethodRecipe')}</div>
                      {m.reagents.map((r, ri) => (
                        <div key={ri} className="mb-1 flex items-center gap-2">
                          <select
                            className={`${inputCls} flex-1`}
                            value={r.reagentId}
                            onChange={(e) => updateExtra(idx, { reagents: m.reagents.map((x, i) => (i === ri ? { ...x, reagentId: e.target.value } : x)) })}
                          >
                            <option value="">{t('admin:invMethodPickReagent')}</option>
                            {reagents.map((rg) => (
                              <option key={rg.id} value={rg.id}>{rg.name}</option>
                            ))}
                          </select>
                          <DecimalInput
                            className={`${inputCls} w-24`}
                            placeholder={t('admin:invMethodQty')}
                            value={r.qtyPerTest}
                            onValueChange={(v) => updateExtra(idx, { reagents: m.reagents.map((x, i) => (i === ri ? { ...x, qtyPerTest: v } : x)) })}
                          />
                          <button type="button" className={`${btnGhost} text-rose-600`} onClick={() => updateExtra(idx, { reagents: m.reagents.filter((_, i) => i !== ri) })}>✕</button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className={`${btnGhost} mt-1`}
                        onClick={() => updateExtra(idx, { reagents: [...m.reagents, { reagentId: '', qtyPerTest: 0 }] })}
                      >
                        + {t('admin:invMethodAddReagent')}
                      </button>
                    </div>
                  ))}
                  <button type="button" className={btnGhost} onClick={addExtraMethod}>
                    + {t('admin:invMethodAdd')}
                  </button>
                </div>
              )}
            </div>
            <div className="confirm-footer">
              <button type="button" className={btnGhost} onClick={() => setIndEdit(null)} disabled={indBusy}>
                {t('admin:cancel')}
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={saveInd}
                disabled={indBusy || !indForm.name.trim()}
              >
                {indBusy ? t('admin:saving') : t('admin:invSave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && tab === 'shopping' && (
        <div className="rounded-xl border border-slate-100 bg-white p-4">
          <p className="mb-3 text-sm text-slate-500">{t('admin:invShoppingHint')}</p>
          {shopping.length === 0 ? (
            <p className="text-sm text-emerald-700">✓ {t('admin:invShoppingEmpty')}</p>
          ) : (
            <div className="flex flex-col">
              <div className="hidden grid-cols-[minmax(0,1fr)_120px_120px_140px_110px] gap-2 px-2 pb-2 text-[11px] uppercase tracking-wide text-slate-400 sm:grid">
                <span>{t('admin:invColName')}</span>
                <span>{t('admin:invColStock')}</span>
                <span>{t('admin:invColMin')}</span>
                <span>{t('admin:invColNeed')}</span>
                <span>{t('admin:invColPacks')}</span>
              </div>
              {shopping.map(({ reagent: r, packs, buyQty }) => (
                <div key={r.id} className="grid grid-cols-2 items-center gap-2 border-t border-slate-100 px-2 py-2 text-sm sm:grid-cols-[minmax(0,1fr)_120px_120px_140px_110px]">
                  <div className="col-span-2 min-w-0 sm:col-span-1">
                    <div className="font-medium text-slate-800">{r.name}</div>
                    {r.supplier && <div className="text-[11px] text-slate-400">{r.supplier}</div>}
                  </div>
                  <span className="text-slate-700">{fmtQty(r.stockQty)} {r.unit}</span>
                  <span className="text-slate-500">{fmtQty(r.minLevel)} {r.unit}</span>
                  <span className="font-medium text-amber-700">{fmtQty(buyQty)} {r.unit}</span>
                  <span className="text-slate-600">{packs !== null ? `${packs} × ${fmtQty(r.packSize || 0)} ${r.unit}` : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
